import React, { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import { logActivity } from "@/lib/activityLog";
import { ModalShell, Field, TextInput, ModalFooter, ErrorBanner } from "../shared/FormElements";

// onSaved receives the newly created client row.
// onSavedAndAddProject receives it too, and the caller decides to open the project wizard next.
export default function AddClientModal({ onClose, onSaved, onSavedAndAddProject }) {
  const { profile } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function validate() {
    if (!companyName.trim() || !contactName.trim() || !contactEmail.trim()) {
      setError("Company name, contact name, and contact email are required.");
      return false;
    }
    return true;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    setError(null);
    const { data, error: insertError } = await supabase
      .from("clients")
      .insert({
        company_name: companyName.trim(),
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        industry: industry.trim() || null,
        lifecycle_status: "new",
      })
      .select()
      .single();
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    logActivity({
      actorId: profile?.id,
      clientId: data.id,
      action: "Added client",
      details: data.company_name,
    });
    return data;
  }

  async function handleSave() {
    const client = await save();
    if (client) onSaved(client);
  }

  async function handleSaveAndAddProject() {
    const client = await save();
    if (client) onSavedAndAddProject(client);
  }

  return (
    <ModalShell
      title="Add client"
      subtitle="Just the basics, projects get added separately"
      onClose={onClose}
    >
      <ErrorBanner error={error} />
      <Field label="Company name">
        <TextInput value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Northwind Retail" />
      </Field>
      <Field label="Contact name">
        <TextInput value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Priya Shah" />
      </Field>
      <Field label="Contact email" hint="This becomes the client's single login (the auth account itself is created separately in Supabase for now).">
        <TextInput type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="priya@northwind.com" />
      </Field>
      <Field label="Industry (optional)">
        <TextInput value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="E-commerce, home goods" />
      </Field>
      <ModalFooter
        onClose={onClose}
        onSave={handleSave}
        saving={saving}
        saveLabel="Save client"
        secondary={
          <button
            onClick={handleSaveAndAddProject}
            disabled={saving}
            className="border border-input rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60"
          >
            Save &amp; add project
          </button>
        }
      />
    </ModalShell>
  );
}
